// The music transport across a page change.
//
// The client is more than one page: the landing, the hub and the game live
// on `index.html`, the arcade lobby and its match on `arcade.html`, and a
// move between them is a real navigation. The page dies, and the audio
// director with it, so the menu music would start again at the first track
// every time the player walks from the hub to the arcade and back. This
// module is the small record that stops that: the page writes where the
// music stood when it hides, and the next page picks it up at boot.
//
// The record lives in `sessionStorage` for two reasons. It is per tab, so a
// second tab keeps its own music and does not fight for one cursor. And it
// dies with the tab, so a visit tomorrow starts clean.
//
// The read consumes the record. A handoff is true for one navigation only:
// if the next page does not use it — a different playlist, a track the
// playlist no longer carries — the record must not stay behind and move the
// music at some later boot.

import { isObject } from '../net/json';
import { readStore, removeStore, writeStore } from '../ui/localstore';

/** The one sessionStorage key. Same convention as `aquaphobia.settings`. */
export const HANDOFF_KEY = 'aquaphobia.musicHandoff';

/** Where the music stood when the last page hid. */
export interface MusicHandoff {
  /** The playlist id the record belongs to. The restore applies only when
   *  the next page selects this same playlist on its own. */
  playlist: string;
  /** Index into the playlist's tracks, not into the shuffled play order. */
  track: number;
  /** Seconds into that track. */
  time: number;
  /** Was the transport running? A paused page hands over a paused page. */
  playing: boolean;
  /** Did the player pause or stop it, with the media card (R396)? The menu
   *  screens draw no transport, so `playing` false without this flag means
   *  "the page was never unlocked" — and the next page may then start its
   *  own music, where the browser allows it. */
  paused: boolean;
  /** Was shuffle on? The next page rebuilds an order with the same rule. */
  shuffle: boolean;
}

/**
 * Parses a stored record. Anything that is not handoff-shaped answers null,
 * on the same defensive rule as `parseSettings`: a blob written by another
 * build must degrade, never throw. A field that is present but wrong falls
 * back to its own zero, because a bad position is still a usable playlist.
 */
export function parseHandoff(raw: string | null): MusicHandoff | null {
  if (!raw) return null;
  let obj: Record<string, unknown>;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isObject(parsed)) return null;
    obj = parsed;
  } catch {
    return null;
  }
  const playlist = obj.playlist;
  if (typeof playlist !== 'string' || playlist === '') return null;
  const track = obj.track;
  const time = obj.time;
  return {
    playlist,
    track: typeof track === 'number' && Number.isInteger(track) && track >= 0 ? track : 0,
    time: typeof time === 'number' && isFinite(time) && time >= 0 ? time : 0,
    playing: !!obj.playing,
    paused: !!obj.paused,
    shuffle: !!obj.shuffle,
  };
}

/**
 * Reads the record and removes it. The removal is the point: a handoff is
 * good for the one boot that follows the page change it was written for.
 */
export function readHandoff(): MusicHandoff | null {
  // A private window or a full store reads null: the next page starts at
  // track one.
  const raw = readStore(HANDOFF_KEY, 'session');
  removeStore(HANDOFF_KEY, 'session');
  return parseHandoff(raw);
}

/** Writes the record. A store that refuses only costs the handoff: the
 *  music restarts, nothing worse. */
export function writeHandoff(h: MusicHandoff): void {
  writeStore(HANDOFF_KEY, JSON.stringify(h), 'session');
}
